import type { DeathCause } from './Death';

/** HUD contract. Integrator merges Vitals + DayClock into this. */
export type SurvivalSnapshot = {
  hp: number;
  hunger: number;
  thirst: number;
  todMinutes: number;
  dead: boolean;
  night: boolean;
  cause: DeathCause | null;
};

export const VITAL_MAX = 100;
export const HP_START = 100;
export const HUNGER_START = 80;
export const THIRST_START = 75;
export const STAMINA_START = 100;
export const FATIGUE_START = 100;

/** Real-time drain. 1.25 / min × 80 min = 100 (full bar). */
export const HUNGER_DRAIN_PER_MIN = 1.25;
/** Real-time drain. Faster than hunger. */
export const THIRST_DRAIN_PER_MIN = 2.0;

export const HUNGER_GRACE_S = 60;
export const THIRST_GRACE_S = 30;
export const HUNGER_HP_PER_S = 0.4;
export const THIRST_HP_PER_S = 0.8;

/** Loop calls `tick(Clock.DT * n)`; drain applies once per whole second. */
export const VITALS_HZ = 1;

const HUNGER_PER_S = HUNGER_DRAIN_PER_MIN / 60;
const THIRST_PER_S = THIRST_DRAIN_PER_MIN / 60;

export type VitalsInit = {
  hp?: number;
  hunger?: number;
  thirst?: number;
  stamina?: number;
  fatigue?: number;
};

export class Vitals {
  private _hp: number;
  private _hunger: number;
  private _thirst: number;
  private _stamina: number;
  private _fatigue: number;
  private _dead = false;
  private _cause: DeathCause | null = null;
  private pending = 0;
  private hungerEmptyS = 0;
  private thirstEmptyS = 0;
  private thirstMult = 1;

  constructor(init: VitalsInit = {}) {
    this._hp = clampVital(init.hp ?? HP_START);
    this._hunger = clampVital(init.hunger ?? HUNGER_START);
    this._thirst = clampVital(init.thirst ?? THIRST_START);
    this._stamina = clampVital(init.stamina ?? STAMINA_START);
    this._fatigue = clampVital(init.fatigue ?? FATIGUE_START);
  }

  get hp(): number {
    return this._hp;
  }

  get hunger(): number {
    return this._hunger;
  }

  get thirst(): number {
    return this._thirst;
  }

  get stamina(): number {
    return this._stamina;
  }

  get fatigue(): number {
    return this._fatigue;
  }

  get dead(): boolean {
    return this._dead;
  }

  get cause(): DeathCause | null {
    return this._cause;
  }

  /** True when `amount` was subtracted. Missing / non-finite amount is ignored. */
  spendStamina(amount: number): boolean {
    if (!Number.isFinite(amount) || amount <= 0) {
      return true;
    }
    if (this._stamina < amount) {
      return false;
    }
    this._stamina -= amount;
    return true;
  }

  addStamina(delta: number): void {
    if (!Number.isFinite(delta) || delta === 0) {
      return;
    }
    this._stamina = clampVital(this._stamina + delta);
  }

  /** Immediate combat / spit / fall damage. Drain still runs at 1 Hz. */
  hurt(amount: number, cause: DeathCause = 'wound'): void {
    if (this._dead || !Number.isFinite(amount) || amount <= 0) {
      return;
    }
    this._hp = Math.max(0, this._hp - amount);
    if (this._hp <= 0) {
      this._hp = 0;
      this._dead = true;
      this._cause = cause;
    }
  }

  /** Instant death with a reason the veil can show. */
  kill(cause: DeathCause): void {
    if (this._dead) {
      return;
    }
    this._hp = 0;
    this._dead = true;
    this._cause = cause;
  }

  /** Clear death and restore meters. Inventory stays with the player. */
  revive(hp: number, hunger: number, thirst: number): void {
    this._hp = clampVital(hp);
    this._hunger = clampVital(hunger);
    this._thirst = clampVital(thirst);
    this._dead = this._hp <= 0;
    this._cause = this._dead ? 'wound' : null;
    this.pending = 0;
    if (this._hunger > 0) {
      this.hungerEmptyS = 0;
    }
    if (this._thirst > 0) {
      this.thirstEmptyS = 0;
    }
  }

  /** Accumulate real seconds; apply drain at 1 Hz. `thirstMult` is heat / poison. */
  tick(dtSeconds: number, thirstMult = 1): void {
    if (!Number.isFinite(dtSeconds) || dtSeconds <= 0) {
      return;
    }
    this.thirstMult = Number.isFinite(thirstMult) && thirstMult > 0 ? thirstMult : 1;
    this.pending += dtSeconds;
    while (this.pending >= 1) {
      this.pending -= 1;
      this.stepSecond();
    }
  }

  snapshot(clock: { readonly todMinutes: number; readonly isNight: boolean }): SurvivalSnapshot {
    return survivalSnapshot(this, clock);
  }

  addThirst(delta: number): void {
    this._thirst = clampVital(this._thirst + delta);
    if (this._thirst > 0) {
      this.thirstEmptyS = 0;
    }
  }

  addHunger(delta: number): void {
    this._hunger = clampVital(this._hunger + delta);
    if (this._hunger > 0) {
      this.hungerEmptyS = 0;
    }
  }

  setFatigue(value: number): void {
    this._fatigue = clampVital(value);
  }

  private stepSecond(): void {
    this._hunger = clampVital(this._hunger - HUNGER_PER_S);
    this._thirst = clampVital(this._thirst - THIRST_PER_S * this.thirstMult);
    if (this._dead) {
      return;
    }

    if (this._hunger <= 0) {
      this.hungerEmptyS += 1;
      if (this.hungerEmptyS > HUNGER_GRACE_S) {
        this._hp -= HUNGER_HP_PER_S;
      }
    } else {
      this.hungerEmptyS = 0;
    }

    if (this._thirst <= 0) {
      this.thirstEmptyS += 1;
      if (this.thirstEmptyS > THIRST_GRACE_S) {
        this._hp -= THIRST_HP_PER_S;
      }
    } else {
      this.thirstEmptyS = 0;
    }

    if (this._hp <= 0) {
      this._hp = 0;
      this._dead = true;
      this._cause = this.thirstEmptyS > this.hungerEmptyS ? 'thirst' : 'hunger';
    }
  }
}

export function survivalSnapshot(
  vitals: Vitals,
  clock: { readonly todMinutes: number; readonly isNight: boolean },
): SurvivalSnapshot {
  return {
    hp: vitals.hp,
    hunger: vitals.hunger,
    thirst: vitals.thirst,
    todMinutes: clock.todMinutes,
    dead: vitals.dead,
    night: clock.isNight,
    cause: vitals.cause,
  };
}

function clampVital(n: number): number {
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(VITAL_MAX, n));
}
